/**
 * The two axes the internal shells can be switched along: which environment the app is served
 * from, and which Focal tool is open. `ForgeContextSwitcher` renders both from these builders.
 *
 * Every environment link is rebuilt from `origin + pathname + search + hash`, so switching
 * environment lands on the same page with its filters and anchor intact.
 */
export type ForgeEnvironmentId = 'prod' | 'staging' | 'local';

export type ForgeAppId = 'forge' | 'control' | 'eval' | 'archon';

export type LocationLike = Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>;

export interface ForgeEnvironmentLink {
  id: ForgeEnvironmentId;
  /**
   * The name, lowercase, as shown on the chip and in the menu. Lowercase in the data rather than
   * via `text-transform`, so the visible text and the accessible name are the same string.
   */
  label: string;
  /** Abbreviation for the collapsed sidebar, where only ~26px is available. */
  shortLabel: string;
  /** One line saying what the environment is, shown under the name in the menu. */
  description: string;
  title: string;
  href: string;
  isActive: boolean;
}

export interface ForgeAppLink {
  id: ForgeAppId;
  label: string;
  shortLabel: string;
  description: string;
  title: string;
  href: string;
  isActive: boolean;
}

const FORGE_ENVIRONMENTS: Array<
  Pick<ForgeEnvironmentLink, 'id' | 'label' | 'shortLabel' | 'description' | 'title'> & {
    origin: string;
  }
> = [
  {
    id: 'prod',
    label: 'production',
    shortLabel: 'prd',
    description: 'Live data, real stores.',
    origin: 'https://control.focal.systems',
    title: 'environment: production',
  },
  {
    id: 'staging',
    label: 'staging',
    shortLabel: 'stg',
    description: 'Safe copy for trying changes out.',
    origin: 'https://control.staging.focal.dev',
    title: 'environment: staging',
  },
  {
    id: 'local',
    label: 'local',
    shortLabel: 'loc',
    description: 'The app running on this machine.',
    origin: 'http://localhost:3002',
    title: 'environment: local',
  },
];

const LOCAL_ORIGIN_PATTERN = /^http:\/\/(?:localhost|127\.0\.0\.1):\d+$/;

const FORGE_APP_LINKS: Array<Omit<ForgeAppLink, 'isActive'>> = [
  {
    id: 'forge',
    label: 'forge',
    shortLabel: 'f',
    description: 'Labeling tools, task lists and dashboards.',
    title: 'tool: forge',
    href: '/forge',
  },
  {
    id: 'control',
    label: 'control',
    shortLabel: 'c',
    description: 'The store-facing app, by store.',
    title: 'tool: control',
    href: '/stores',
  },
  {
    id: 'eval',
    label: 'eval',
    shortLabel: 'e',
    description: 'Model evaluation runs and comparisons.',
    title: 'tool: eval',
    href: '/eval',
  },
  {
    id: 'archon',
    label: 'archon',
    shortLabel: 'ar',
    description: 'Match cost, volume and savings.',
    title: 'tool: archon',
    href: '/archon',
  },
];

const isLocalForgeOrigin = (origin: string): boolean => LOCAL_ORIGIN_PATTERN.test(origin);

export const getCurrentForgeEnvironment = (origin: string): ForgeEnvironmentId | undefined => {
  if (origin === 'https://control.focal.systems') return 'prod';
  if (origin === 'https://control.staging.focal.dev') return 'staging';
  if (isLocalForgeOrigin(origin)) return 'local';
  return undefined;
};

const getForgeEnvironmentOrigin = (
  environment: (typeof FORGE_ENVIRONMENTS)[number],
  currentOrigin: string
): string => {
  // A slot runs on its own port, so "Local" has to mean the port already in the address bar.
  if (environment.id === 'local' && isLocalForgeOrigin(currentOrigin)) {
    return currentOrigin;
  }

  return environment.origin;
};

export const getCurrentForgeRoute = (location: LocationLike): string => {
  // The links are titled "Open this page in <environment>", so they carry the current
  // route across. This used to fall back to '/forge' for anything outside the Forge,
  // Eval and Archon prefixes, which was unreachable while only those shells rendered the
  // switcher — Control now does too, and sending a reader of /stores to Forge in another
  // environment is not what the link says it does. Only the bare root, which redirects
  // on arrival anyway, has no page worth preserving.
  if (location.pathname === '/' || location.pathname === '') {
    return '/forge';
  }

  return `${location.pathname}${location.search}${location.hash}`;
};

export const buildForgeEnvironmentLinks = (
  location: LocationLike = window.location
): ForgeEnvironmentLink[] => {
  const route = getCurrentForgeRoute(location);
  const activeEnvironment = getCurrentForgeEnvironment(location.origin);

  return FORGE_ENVIRONMENTS.map(({ origin, ...environment }) => ({
    ...environment,
    href: `${getForgeEnvironmentOrigin({ ...environment, origin }, location.origin)}${route}`,
    isActive: environment.id === activeEnvironment,
  }));
};

export const buildForgeAppLinks = (
  location: LocationLike = window.location,
  options: { includeArchon?: boolean } = {}
): ForgeAppLink[] =>
  FORGE_APP_LINKS.filter((link) => options.includeArchon || link.id !== 'archon').map((link) => ({
    ...link,
    isActive:
      (link.id === 'forge' && location.pathname.startsWith('/forge')) ||
      (link.id === 'eval' && location.pathname.startsWith('/eval')) ||
      (link.id === 'archon' && location.pathname.startsWith('/archon')) ||
      (link.id === 'control' &&
        !location.pathname.startsWith('/forge') &&
        !location.pathname.startsWith('/eval') &&
        !location.pathname.startsWith('/archon')),
  }));
